import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSearchParams } from "react-router-dom";
import ReactECharts from "echarts-for-react";
import { useGetPlayerByIdQuery } from "../store/api/mlbApi";
import { useComparePlayerSearch } from "../hooks/useComparePlayerSearch";
import { useSavePlayerChart } from "../hooks/useSavePlayerChart";
import { formatPlayerName } from "../utils/textUtils";
import { colors } from "../theme";
import { useAnalytics } from "../hooks/useAnalytics";
import { getStatCategories } from "../hooks/useLeaderboardData";
import { STAT_CATEGORIES, getPlayerDataFieldName } from "../constants/stats";
import { buildEchartsOptions } from "../utils/chartTheme";
import CompareHeadToHeadTable from "../components/compare/CompareHeadToHeadTable";
import MlbCompareInspiration from "../components/compare/MlbCompareInspiration";
import {
  CompareChartCard,
  ComparePageFrame,
  CompareSlotsBar,
  CompareVsSeparator,
} from "../components/compare/ComparePageChrome";
import CommunityDebateCta from "../components/community/CommunityDebateCta";
import {
  COMPARE_CHART_HEIGHT,
  COMPARE_MAX_PLAYERS,
  COMPARE_PAGE_SUBTITLE,
  COMPARE_PAGE_TITLE,
  getCompareTimeframe,
} from "../components/compare/compareShell";
import {
  CompareHeadshotRing,
  CompareSlotPlaceholder,
  getPlayerAccent,
  SELECTION_HEADSHOT_SIZE,
} from "../components/compare/comparePlayerVisuals";

const Compare = () => {
  const [searchParams] = useSearchParams();
  const presetLoadedRef = useRef(false);
  const lastComparedIdsRef = useRef("");
  const [selectedStat, setSelectedStat] = useState("homeruns");
  const [statCategory, setStatCategory] = useState(STAT_CATEGORIES.HITTING);
  const [showComparison, setShowComparison] = useState(false);
  const [normalizeByCareer, setNormalizeByCareer] = useState(true);

  // Analytics hook
  const { trackPlayerSelected, trackComparePlayers, trackWidgetSaved } =
    useAnalytics();

  // Widget save state
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [widgetTitle, setWidgetTitle] = useState("");
  const [saveStatus, setSaveStatus] = useState({
    show: false,
    success: false,
    message: "",
  });

  // Use the enhanced compare player search hook
  const {
    playerSlots,
    handleSearchInputChange,
    handlePlayerSelect,
    handlePopularPlayerSelect,
    handleAddPlayer,
    handleRemovePlayer,
    updatePlayerData,
    getSearchQueryForSlot,
    setPlayerIds,
  } = useComparePlayerSearch(COMPARE_MAX_PLAYERS);

  useEffect(() => {
    if (presetLoadedRef.current) return;

    const playersParam = searchParams.get("players");
    if (!playersParam) return;

    const playerIds = playersParam
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);

    if (playerIds.length > 0) {
      setPlayerIds(playerIds);
      presetLoadedRef.current = true;
    }
  }, [searchParams, setPlayerIds]);

  const loadedPlayerCount = playerSlots.filter((slot) => slot.data).length;
  const canAutoCompare = loadedPlayerCount >= 2;
  const isLoadingComparison = playerSlots.some((slot) => slot.id && !slot.data);

  // Auto-compare when 2+ players are loaded (includes deep-link presets)
  useEffect(() => {
    if (loadedPlayerCount < 2) {
      setShowComparison(false);
      lastComparedIdsRef.current = "";
      return;
    }

    const slotsWithIds = playerSlots.filter((slot) => slot.id);
    const allIdsLoaded =
      slotsWithIds.length > 0 && slotsWithIds.every((slot) => slot.data);

    if (!allIdsLoaded) return;

    setShowComparison(true);

    const ids = playerSlots
      .filter((slot) => slot.data)
      .map((slot) => slot.id)
      .sort()
      .join(",");

    if (ids && ids !== lastComparedIdsRef.current) {
      lastComparedIdsRef.current = ids;
      const selectedPlayers = playerSlots
        .filter((slot) => slot.data)
        .map((slot) => formatPlayerName(slot.data) || slot.id);
      trackComparePlayers(
        selectedPlayers.length,
        selectedPlayers,
        selectedStat
      );
    }
  }, [playerSlots, loadedPlayerCount, selectedStat, trackComparePlayers]);

  // Widget save hook
  const { savePlayerChart } = useSavePlayerChart("home");
  const timeframe = getCompareTimeframe(normalizeByCareer);

  // Get search queries for each slot using the hook
  const searchQuery1 = getSearchQueryForSlot(0);
  const searchQuery2 = getSearchQueryForSlot(1);
  const searchQuery3 = getSearchQueryForSlot(2);
  const searchQuery4 = getSearchQueryForSlot(3);
  const searchQuery5 = getSearchQueryForSlot(4);

  // Player data queries for each slot (support up to 5 players)
  const player1Query = useGetPlayerByIdQuery(playerSlots[0]?.id, {
    skip: !playerSlots[0]?.id,
  });
  const player2Query = useGetPlayerByIdQuery(playerSlots[1]?.id, {
    skip: !playerSlots[1]?.id,
  });
  const player3Query = useGetPlayerByIdQuery(playerSlots[2]?.id, {
    skip: !playerSlots[2]?.id,
  });
  const player4Query = useGetPlayerByIdQuery(playerSlots[3]?.id, {
    skip: !playerSlots[3]?.id,
  });
  const player5Query = useGetPlayerByIdQuery(playerSlots[4]?.id, {
    skip: !playerSlots[4]?.id,
  });

  // Get stat categories from our leaderboard hook
  const statCategories = getStatCategories();

  // Get current stat options based on selected category
  const currentStatOptions =
    statCategories[statCategory] || statCategories.hitting;

  // Enhanced player selection handler that includes analytics tracking
  const handlePlayerSelectWithAnalytics = (index, newValue) => {
    handlePlayerSelect(index, newValue);

    // Track player selection for analytics
    if (newValue) {
      trackPlayerSelected(
        newValue.playerId,
        formatPlayerName(newValue),
        index,
        "compare_page"
      );
    }
  };

  // Handle stat category change
  const handleStatCategoryChange = (newCategory) => {
    setStatCategory(newCategory);
    // Reset to first stat in the new category
    const newStats = statCategories[newCategory];
    if (newStats && newStats.length > 0) {
      setSelectedStat(newStats[0].name);
    }
  };

  // Handle stat selection change
  const handleStatChange = (newStat) => {
    setSelectedStat(newStat);
  };

  // Update player data when queries complete
  const player1Id = playerSlots[0]?.id;
  const player2Id = playerSlots[1]?.id;
  const player3Id = playerSlots[2]?.id;
  const player4Id = playerSlots[3]?.id;
  const player5Id = playerSlots[4]?.id;

  React.useEffect(() => {
    if (player1Query.data && player1Id) {
      updatePlayerData(0, player1Query.data);
    }
  }, [player1Query.data, player1Id, updatePlayerData]);

  React.useEffect(() => {
    if (player2Query.data && player2Id) {
      updatePlayerData(1, player2Query.data);
    }
  }, [player2Query.data, player2Id, updatePlayerData]);

  React.useEffect(() => {
    if (player3Query.data && player3Id) {
      updatePlayerData(2, player3Query.data);
    }
  }, [player3Query.data, player3Id, updatePlayerData]);

  React.useEffect(() => {
    if (player4Query.data && player4Id) {
      updatePlayerData(3, player4Query.data);
    }
  }, [player4Query.data, player4Id, updatePlayerData]);

  React.useEffect(() => {
    if (player5Query.data && player5Id) {
      updatePlayerData(4, player5Query.data);
    }
  }, [player5Query.data, player5Id, updatePlayerData]);

  const getStatLabel = (statType) => {
    const statOption = currentStatOptions.find(
      (option) => option.name === statType
    );
    return statOption ? statOption.displayName : statType;
  };

  // Helper function to get stat value from season data using standardized mapping
  const getSeasonStatValue = (seasonData, statType) => {
    if (!seasonData) return 0;

    // Convert leaderboard stat name to player data field name
    const playerDataFieldName = getPlayerDataFieldName(statType);

    // Extract the stat value with fallback to original stat name
    const statValue =
      seasonData[playerDataFieldName] || seasonData[statType] || 0;

    return parseFloat(statValue) || 0;
  };

  const shouldRenderChart = () => {
    return playerSlots.some(
      (slot) =>
        slot.data &&
        slot.data.stats?.hitting?.seasons &&
        Object.keys(slot.data.stats.hitting.seasons).length > 0
    );
  };

  const isLoadingPresetComparison =
    presetLoadedRef.current && isLoadingComparison;

  const renderChart = () => {
    if (!showComparison || !shouldRenderChart()) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          {isLoadingPresetComparison ? (
            <CircularProgress />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {isLoadingComparison
                ? "Loading player stats…"
                : loadedPlayerCount === 1
                  ? "Add one more player to start comparing"
                  : "Search or pick players below to compare"}
            </Typography>
          )}
        </Box>
      );
    }

    // Get all available years across all players
    let allYears;
    if (normalizeByCareer) {
      // For career-normalized view, use career years (1, 2, 3, etc.)
      const maxCareerYears = Math.max(
        ...playerSlots
          .filter((slot) => slot.data)
          .map((slot) => {
            const seasons = slot.data.stats[statCategory]?.seasons || {};
            return Object.keys(seasons).length;
          })
      );
      allYears = Array.from({ length: maxCareerYears }, (_, i) =>
        (i + 1).toString()
      );
    } else {
      // For calendar view, use actual years
      allYears = playerSlots
        .filter((slot) => slot.data)
        .reduce((years, slot) => {
          const playerYears = Object.keys(
            slot.data.stats[statCategory]?.seasons || {}
          );
          return [...new Set([...years, ...playerYears])];
        }, [])
        .sort();
    }

    // Create series data for each player
    const series = playerSlots
      .filter((slot) => slot.data)
      .map((slot) => {
        const seasons = slot.data.stats[statCategory]?.seasons || {};

        let data;
        if (normalizeByCareer) {
          // For career-normalized view, map by career year order
          const sortedYears = Object.keys(seasons).sort();
          data = allYears.map((careerYear, index) => {
            const actualYear = sortedYears[index];
            const seasonData = actualYear ? seasons[actualYear] : null;
            return getSeasonStatValue(seasonData, selectedStat);
          });
        } else {
          // For calendar view, map by actual years
          data = allYears.map((year) => {
            const seasonData = seasons[year];
            return getSeasonStatValue(seasonData, selectedStat);
          });
        }

        return {
          name: formatPlayerName(slot.data),
          type: "line",
          data: data,
          connectNulls: true, // Skip missing data points
          symbol: "circle",
          symbolSize: 8,
          emphasis: {
            focus: "series",
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        };
      });

    return buildEchartsOptions({
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
      },
      legend: {
        data: playerSlots
          .filter((slot) => slot.data)
          .map((slot) => formatPlayerName(slot.data)),
        top: 0,
      },
      grid: {
        left: "5%",
        right: "4%",
        bottom: "12%",
        containLabel: true,
        top: 60,
      },
      xAxis: {
        type: "category",
        data: allYears,
        name: normalizeByCareer ? "Career Year" : "Year",
        nameLocation: "middle",
        nameGap: 45,
        axisLabel: {
          show: true,
          margin: 15,
        },
      },
      yAxis: {
        type: "value",
        name: getStatLabel(selectedStat),
        nameLocation: "middle",
        nameGap: 60,
        axisLabel: {
          show: true,
          margin: 15,
        },
      },
      series: series,
    });
  };

  const getSearchQuery = (index) => {
    switch (index) {
      case 0:
        return searchQuery1;
      case 1:
        return searchQuery2;
      case 2:
        return searchQuery3;
      case 3:
        return searchQuery4;
      case 4:
        return searchQuery5;
      default:
        return { data: [], isLoading: false, error: null };
    }
  };

  const getPlayerQuery = (index) => {
    switch (index) {
      case 0:
        return player1Query;
      case 1:
        return player2Query;
      case 2:
        return player3Query;
      case 3:
        return player4Query;
      case 4:
        return player5Query;
      default:
        return { data: null, isLoading: false, error: null };
    }
  };

  // Widget save functions
  const handleSaveWidget = () => {
    const validPlayers = playerSlots
      .filter((slot) => slot.data)
      .map((slot) => slot.data);

    if (validPlayers.length === 0) {
      setSaveStatus({
        show: true,
        success: false,
        message: "No players selected for comparison",
      });
      return;
    }

    // Generate default title if none provided
    const defaultTitle =
      validPlayers.length === 1
        ? `${formatPlayerName(validPlayers[0])} - ${getStatLabel(selectedStat)}`
        : `${validPlayers
            .map((p) => formatPlayerName(p))
            .join(" vs ")} - ${getStatLabel(selectedStat)}`;

    setWidgetTitle(widgetTitle || defaultTitle);
    setIsSaveDialogOpen(true);
  };

  const handleConfirmSave = async () => {
    try {
      // Just pass the player IDs and basic info - no need for full data structure
      const validPlayers = playerSlots
        .filter((slot) => slot.data)
        .map((slot) => slot.data);

      const chartConfig = {
        title: widgetTitle,
        players: validPlayers,
        selectedStat,
        statCategory,
        normalizeByCareer,
        type: validPlayers.length === 1 ? "single" : "comparison",
      };

      const result = await savePlayerChart(chartConfig);

      if (result.success) {
        // Track successful widget save
        trackWidgetSaved(
          "player_chart",
          validPlayers.length === 1 ? "single_player" : "comparison",
          selectedStat,
          validPlayers.length
        );

        setSaveStatus({
          show: true,
          success: true,
          message: "Chart saved as widget successfully!",
        });
        setIsSaveDialogOpen(false);
        setWidgetTitle("");
      } else {
        setSaveStatus({
          show: true,
          success: false,
          message: result.error || "Failed to save widget",
        });
      }
    } catch (error) {
      setSaveStatus({
        show: true,
        success: false,
        message: "An error occurred while saving the widget",
      });
    }
  };

  const handleCloseSaveDialog = () => {
    setIsSaveDialogOpen(false);
    setWidgetTitle("");
  };

  const handleCloseSnackbar = () => {
    setSaveStatus({ show: false, success: false, message: "" });
  };

  const renderPlayerOption = (props, option) => {
    const { key, ...otherProps } = props;
    return (
      <li key={key} {...otherProps}>
        <div>
          <div style={{ fontWeight: "bold" }}>
            {formatPlayerName(option)}
          </div>
          <div style={{ fontSize: "0.875rem", opacity: 0.7 }}>
            Career: {option.stats?.hitting?.career?.years || "N/A"} | HR:{" "}
            {option.stats?.hitting?.career?.homeruns || "N/A"} | AVG:{" "}
            {option.stats?.hitting?.career?.battingAverage || "N/A"}
          </div>
        </div>
      </li>
    );
  };

  return (
    <ComparePageFrame title={COMPARE_PAGE_TITLE} subtitle={COMPARE_PAGE_SUBTITLE}>
      <CompareSlotsBar
        onAdd={handleAddPlayer}
        canAdd={playerSlots.length < COMPARE_MAX_PLAYERS}
        atMax={playerSlots.length >= COMPARE_MAX_PLAYERS}
      >
        {playerSlots.map((slot, index) => {
          const searchQuery = getSearchQuery(index);
          const playerQuery = getPlayerQuery(index);
          return (
            <React.Fragment key={index}>
              {index > 0 && <CompareVsSeparator />}
              <Box
                sx={{
                  flex: "1 1 180px",
                  minWidth: 160,
                  maxWidth: 280,
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {slot.id ? (
                    <CompareHeadshotRing
                      playerId={slot.id}
                      size={SELECTION_HEADSHOT_SIZE}
                      accent={getPlayerAccent(index)}
                      showLoading={playerQuery.isLoading}
                    />
                  ) : (
                    <CompareSlotPlaceholder
                      size={SELECTION_HEADSHOT_SIZE}
                      accent={getPlayerAccent(index)}
                    />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Autocomplete
                      freeSolo
                      fullWidth
                      size="small"
                      options={searchQuery?.data || []}
                      value={slot.data}
                      loading={searchQuery?.isLoading || playerQuery.isLoading}
                      getOptionLabel={(option) => formatPlayerName(option)}
                      isOptionEqualToValue={(option, value) =>
                        option?.playerId === value?.playerId
                      }
                      onChange={(_, newValue) =>
                        handlePlayerSelectWithAnalytics(index, newValue)
                      }
                      onInputChange={(_, newInputValue) =>
                        handleSearchInputChange(index, newInputValue)
                      }
                      inputValue={slot.inputValue}
                      filterOptions={(options) => options}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={`Player ${index + 1}`}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {playerQuery.isLoading ? (
                                  <CircularProgress color="inherit" size={16} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={renderPlayerOption}
                      noOptionsText="No players found"
                    />
                  </Box>
                </Box>
                {playerSlots.length > 2 && (
                  <IconButton
                    size="small"
                    aria-label={`Remove player ${index + 1}`}
                    onClick={() => handleRemovePlayer(index)}
                    sx={{
                      position: "absolute",
                      top: -10,
                      right: -10,
                      width: 22,
                      height: 22,
                      bgcolor: colors.paper,
                      border: `1px solid ${colors.border}`,
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Box>
            </React.Fragment>
          );
        })}
      </CompareSlotsBar>

      <MlbCompareInspiration
        selectedIds={playerSlots.map((slot) => slot.id)}
        onSelect={handlePopularPlayerSelect}
        onSelectMatchup={setPlayerIds}
      />

      <CommunityDebateCta
        sport="mlb"
        playerIds={playerSlots.map((slot) => slot.id)}
      />

      <CompareChartCard
        groups={[
          { value: STAT_CATEGORIES.HITTING, label: "Hitting" },
          { value: STAT_CATEGORIES.PITCHING, label: "Pitching" },
          { value: STAT_CATEGORIES.FIELDING, label: "Fielding" },
        ]}
        groupValue={statCategory}
        onGroupChange={handleStatCategoryChange}
        stats={currentStatOptions.map((option) => ({
          id: option.name,
          label: option.displayName,
        }))}
        statValue={selectedStat}
        onStatChange={handleStatChange}
        timeframe={timeframe}
        showTimeframe={showComparison && canAutoCompare}
        onToggleTimeframe={() => setNormalizeByCareer(!normalizeByCareer)}
        actions={
          showComparison && shouldRenderChart() ? (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleSaveWidget}
              size="small"
              sx={{ flexShrink: 0 }}
            >
              Save as Widget
            </Button>
          ) : null
        }
      >
        {showComparison && shouldRenderChart() ? (
          <ReactECharts
            option={renderChart()}
            style={{ height: COMPARE_CHART_HEIGHT }}
            notMerge={true}
          />
        ) : (
          renderChart()
        )}
      </CompareChartCard>

      <CompareHeadToHeadTable
        playerSlots={playerSlots}
        statCategory={statCategory}
        selectedStat={selectedStat}
        showComparison={showComparison}
      />

      {/* Save Widget Dialog */}
      <Dialog
        open={isSaveDialogOpen}
        onClose={handleCloseSaveDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save Chart as Widget</DialogTitle>
        <DialogContent>
          <TextField
            label="Widget Title"
            value={widgetTitle}
            onChange={(e) => setWidgetTitle(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="Enter a title for your widget"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSaveDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmSave}
            variant="contained"
            disabled={!widgetTitle.trim()}
          >
            Save Widget
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Snackbar */}
      <Snackbar
        open={saveStatus.show}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={saveStatus.success ? "success" : "error"}
          sx={{ width: "100%" }}
        >
          {saveStatus.message}
        </Alert>
      </Snackbar>
    </ComparePageFrame>
  );
};

export default Compare;
